import React, { useEffect, useMemo, useState } from "react";
import { getData, postData } from "../../services/FetchNodeservices";

export default function JobAssignments() {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const currentUserId = Number(localStorage.getItem("user_id"));

  const fetchData = async () => {
    const res = await getData(`jobassignments/fetch?user_id=${currentUserId}`);
    if (res?.status) setData(res.data);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const uniqueCompanies = useMemo(() => {
    const cos = data.map((r) => r.company).filter(Boolean);
    return Array.from(new Set(cos)).sort();
  }, [data]);

  const uniqueLocations = useMemo(() => {
    const locs = data.map((r) => r.location).filter(Boolean);
    return Array.from(new Set(locs)).sort();
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((row) => {
      // Search term match
      if (search) {
        const text = `${row.profile} ${row.company} ${row.location} ${row.assigned_to_names} ${row.assigned_by_name} ${row.req_id} ${row.id}`.toLowerCase();
        if (!text.includes(search.toLowerCase())) return false;
      }
      // Company filter
      if (filterCompany && row.company !== filterCompany) return false;
      // Location filter
      if (filterLocation && row.location !== filterLocation) return false;
      // Status filter
      if (filterStatus && (row.status || "Pending") !== filterStatus) return false;

      return true;
    });
  }, [data, search, filterCompany, filterLocation, filterStatus]);

  const getStatusColorClass = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-50 text-green-700 border border-green-150";
      case "In Progress":
        return "bg-indigo-50 text-indigo-700 border border-indigo-150";
      case "Cancelled":
        return "bg-red-50 text-red-700 border border-red-150";
      default: // Pending
        return "bg-amber-50 text-amber-700 border border-amber-150";
    }
  };

  return (
    <div className="w-full space-y-6 bg-[#f4f6fa]/30 font-sans">
      
      {/* HEADER */}
      <div>
        <h1 className="text-2xl font-black text-gray-900 tracking-tight">
          Job Assignments
        </h1>
        <p className="text-sm font-semibold text-gray-400 mt-1">
          Review and update status of allocated client mandates and recruiter allocations
        </p>
      </div>

      {/* FILTER BAR CARD */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-2xl shadow-indigo-100/20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {/* SEARCH */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Search Assignments</label>
            <div className="relative">
              <span className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                placeholder="Search profile, company, recruiter..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-xs font-semibold placeholder-gray-450"
              />
            </div>
          </div>

          {/* COMPANY */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Company</label>
            <select
              value={filterCompany}
              onChange={(e) => setFilterCompany(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Companies</option>
              {uniqueCompanies.map((c, i) => (
                <option key={i} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* LOCATION */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Location</label>
            <select
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Locations</option>
              {uniqueLocations.map((l, i) => (
                <option key={i} value={l}>{l}</option>
              ))}
            </select>
          </div>

          {/* STATUS */}
          <div className="w-full">
            <label className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider block mb-2">Status</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-4 py-2 rounded-xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-xs font-semibold"
            >
              <option value="">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* TABLE CARD */}
      <div className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-indigo-100/20 overflow-hidden">
        <div className="w-full overflow-auto" style={{ maxHeight: "70vh" }}>
          <table className="w-full border-collapse text-left text-sm text-gray-750">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-extrabold text-gray-400 uppercase tracking-wider text-left">
                <th className="px-4 py-3.5 w-[70px]">ID</th>
                <th className="px-4 py-3.5 w-[110px]">Req ID</th>
                <th className="px-4 py-3.5">Assigned To</th>
                <th className="px-4 py-3.5">Assigned By</th>
                <th className="px-4 py-3.5">Profile</th>
                <th className="px-4 py-3.5">Company</th>
                <th className="px-4 py-3.5">Location</th>
                <th className="px-4 py-3.5">Assigned At</th>
                <th className="px-4 py-3.5 w-[160px]">Status</th>
                <th className="px-4 py-3.5 w-[90px] text-right">Target</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-6 py-12 text-center text-gray-400 font-semibold">
                    No job assignments found matching filter criteria.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/30 transition text-gray-700">
                    <td className="px-4 py-4 font-semibold text-gray-500">#{row.id}</td>
                    <td className="px-4 py-4 font-extrabold text-gray-900">#{row.req_id}</td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {row.assigned_to_names
                          ? row.assigned_to_names.split(",").map((n, i) => (
                              <span key={i} className="bg-indigo-50 border border-indigo-100/50 text-indigo-650 px-2 py-0.5 rounded-full text-[10px] font-bold">
                                {n}
                              </span>
                            ))
                          : "-"}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-xs font-semibold text-gray-500">{row.assigned_by_name}</td>
                    <td className="px-4 py-4 text-xs font-black text-indigo-950 truncate">{row.profile}</td>
                    <td className="px-4 py-4 text-xs font-bold text-gray-700 truncate">{row.company}</td>
                    <td className="px-4 py-4 text-xs font-medium text-gray-650">{row.location}</td>
                    <td className="px-4 py-4 text-xs font-medium text-gray-500">{row.assigned_at}</td>
                    <td className="px-4 py-4">
                      <select
                        value={row.status || "Pending"}
                        onChange={async (e) => {
                          await postData(`job-assignments/update-status/${row.id}`, {
                            status: e.target.value,
                          });
                          fetchData();
                        }}
                        className={`text-[10px] font-extrabold uppercase px-2.5 py-1 rounded-full border focus:outline-none focus:ring-4 focus:ring-indigo-100 transition cursor-pointer ${getStatusColorClass(row.status || "Pending")}`}
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </td>
                    <td className="px-4 py-4 text-right font-black text-gray-900">{row.target_count || 0}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}